/**
 * Document File Validation
 * Validates document files before upload (matching mobile implementation)
 */

export const MAX_DOCUMENT_SIZE = 50 * 1024 * 1024; // 50 MB

// Must match API validation in MediaValidationService.cs
export const SUPPORTED_DOCUMENT_TYPES: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/jpg': ['.jpg'],  // Some browsers use image/jpg instead of image/jpeg
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

export interface DocumentValidation {
  valid: boolean;
  error?: string;
}

/**
 * Validate a document file for upload
 */
export const validateDocumentFile = (file: File): DocumentValidation => {
  // Check file size
  if (file.size === 0) {
    return {
      valid: false,
      error: 'File is empty. Please select a valid document.',
    };
  }

  if (file.size > MAX_DOCUMENT_SIZE) {
    const maxSizeMB = Math.floor(MAX_DOCUMENT_SIZE / (1024 * 1024));
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File size must be under ${maxSizeMB} MB. Selected file is ${fileSizeMB} MB.`,
    };
  }

  // Check file type
  const isSupported = Object.keys(SUPPORTED_DOCUMENT_TYPES).includes(file.type);

  if (!isSupported) {
    // Also check by extension as fallback
    const extension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    const isSupportedByExtension = extension &&
      Object.values(SUPPORTED_DOCUMENT_TYPES).some(exts => exts.includes(extension));

    if (!isSupportedByExtension) {
      return {
        valid: false,
        error: 'Unsupported file type. Please upload supported document types.',
      };
    }
  }

  return { valid: true };
};

/**
 * Get file extension from filename
 */
export const getFileExtension = (filename: string): string => {
  const match = filename.toLowerCase().match(/\.[^.]+$/);
  return match ? match[0] : '';
};

/**
 * Get accept attribute value for file input
 */
export const getAcceptedFileTypes = (): string => {
  const extensions = Object.values(SUPPORTED_DOCUMENT_TYPES).flat();
  const mimeTypes = Object.keys(SUPPORTED_DOCUMENT_TYPES);
  return [...mimeTypes, ...extensions].join(',');
};
